import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  KeyRound,
  Shield,
  UserPlus,
  Bot,
  PlugZap,
  Cloud,
  FlaskConical,
  Server,
} from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { PlatformAdmin } from '../components/PlatformAdmin'
import { useAuth } from '../lib/auth'

type UserRow = {
  email: string
  name: string
  roles: string[]
  two_factor_enabled: boolean
  status: string
}

type AgentRow = {
  agent_id: string
  name: string
  status: string
  namespace: string
}

type ConnectorRow = {
  provider: string
  category: string
  enabled: boolean
  configured: boolean
  configured_here?: boolean
  inherited_from?: string | null
  fields?: string[]
  public?: Record<string, string>
  last_test_ok?: boolean | null
  last_test_detail?: string | null
}

type KeyScope = 'tenant' | 'project'

type DeployTarget = {
  cloud: string
  label: string
  tier?: string
  allowed_on_plan: boolean
  regions_default: string[]
  why?: string
  connector?: string
}

type DeployReadiness = {
  ready: boolean
  primary?: {
    cloud: string
    keys_configured?: boolean
    last_test_ok?: boolean | null
  } | null
  hyperscalers: Array<{
    cloud: string
    label: string
    allowed_on_plan?: boolean
    keys_configured?: boolean
    last_test_ok?: boolean | null
    selected_primary?: boolean
    why?: string
    regions_default?: string[]
  }>
  edge_and_alt: Array<{
    cloud: string
    label: string
    allowed_on_plan?: boolean
    keys_configured?: boolean
    last_test_ok?: boolean | null
    selected_primary?: boolean
    why?: string
  }>
  note?: string
}

type McpServer = {
  server_id: string
  label: string
  template_id: string
  endpoint?: string | null
  last_test_ok?: boolean | null
  last_test_detail?: string | null
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

type AccessRequest = {
  email: string
  name?: string
  company?: string
  note?: string
  status: 'pending' | 'invited'
  updated_at?: string
  requests?: number
}

const CATEGORY_ORDER = ['ticket', 'ai', 'scm', 'cloud', 'collab', 'mcp'] as const

function secretFieldFor(fields?: string[]): string {
  const prefs = [
    'api_token',
    'api_key',
    'token',
    'bot_token',
    'secret_access_key',
    'client_secret',
    'service_account_json',
  ]
  for (const p of prefs) {
    if (fields?.includes(p)) return p
  }
  return fields?.[0] || 'api_key'
}

export function AdminPage() {
  const { user, loading } = useAuth()
  const [users, setUsers] = useState<UserRow[]>([])
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [connectors, setConnectors] = useState<ConnectorRow[]>([])
  const [targets, setTargets] = useState<DeployTarget[]>([])
  const [mcpServers, setMcpServers] = useState<McpServer[]>([])
  const [mcpTemplates, setMcpTemplates] = useState<
    Array<{ template_id: string; label: string }>
  >([])
  const [primaryCloud, setPrimaryCloud] = useState('aws')
  const [primaryRegion, setPrimaryRegion] = useState('us-east-1')
  const [secondaryCloud, setSecondaryCloud] = useState('')
  const [cloudSecret, setCloudSecret] = useState('')
  const [cloudSecret2, setCloudSecret2] = useState('')
  const [cloudPublic, setCloudPublic] = useState('')
  const [deployReady, setDeployReady] = useState<DeployReadiness | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [accessRequests, setAccessRequests] = useState<AccessRequest[]>([])
  const [inviteRole, setInviteRole] = useState('agent_admin')
  const [agentName, setAgentName] = useState('')
  const [keyScope, setKeyScope] = useState<KeyScope>('project')
  const [keyProvider, setKeyProvider] = useState('github')
  const [keySecretField, setKeySecretField] = useState('token')
  const [keySecret, setKeySecret] = useState('')
  const [keyPublic, setKeyPublic] = useState('')
  const [ghRepo, setGhRepo] = useState('')
  const [ghBranch, setGhBranch] = useState('main')
  const [mcpId, setMcpId] = useState('zendesk_ro')
  const [mcpTemplate, setMcpTemplate] = useState('zendesk_readonly')
  const [mcpEndpoint, setMcpEndpoint] = useState('')
  const [webhookUrl, setWebhookUrl] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function refresh(scope: KeyScope = keyScope) {
    const [u, a, conn, deploy, mcp] = await Promise.all([
      api<UserRow[]>('/api/afos360/admin/users'),
      api<AgentRow[]>('/api/afos360/admin/agents'),
      api<{ connectors: ConnectorRow[] }>(
        `/api/afos360/v1/connectors?scope=${encodeURIComponent(scope)}`,
      ),
      api<{
        targets: DeployTarget[]
        prefs: {
          primary_cloud?: string | null
          primary_region?: string | null
          secondary_cloud?: string | null
        }
        readiness?: DeployReadiness
        platform_home: string
      }>(`/api/afos360/deploy/prefs?scope=${encodeURIComponent(scope)}`),
      api<{
        servers: McpServer[]
        templates: Array<{ template_id: string; label: string }>
      }>(`/api/afos360/v1/mcp?scope=${encodeURIComponent(scope)}`),
    ])
    setUsers(u)
    setAgents(a)
    setConnectors(conn.connectors)
    setTargets(deploy.targets)
    if (deploy.prefs?.primary_cloud) setPrimaryCloud(deploy.prefs.primary_cloud)
    if (deploy.prefs?.primary_region) setPrimaryRegion(deploy.prefs.primary_region)
    if (deploy.prefs?.secondary_cloud) setSecondaryCloud(deploy.prefs.secondary_cloud)
    if (deploy.readiness) setDeployReady(deploy.readiness)
    setMcpServers(mcp.servers)
    setMcpTemplates(mcp.templates)
    if (user?.is_super_admin) {
      const req = await api<{ requests: AccessRequest[] }>('/api/afos360/admin/access-requests')
      setAccessRequests(req.requests)
    }
  }

  useEffect(() => {
    if (user?.is_super_admin || user?.permissions?.includes('users.manage')) {
      void refresh(keyScope).catch((err) =>
        setError(err instanceof Error ? err.message : 'Load failed'),
      )
    }
  }, [user, keyScope])

  useEffect(() => {
    const row = connectors.find((c) => c.provider === keyProvider)
    if (row?.fields) setKeySecretField(secretFieldFor(row.fields))
  }, [keyProvider, connectors])

  if (!loading && !user) return <Navigate to="/signin" replace />
  if (user && !user.is_super_admin && !user.permissions?.includes('users.manage')) {
    return <Navigate to="/app" replace />
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setMessage(null)
    try {
      const result = await api<{
        invite_id: string
        invite_url: string
        otp_demo?: string
        mail: { status: string }
      }>('/api/afos360/admin/invites', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          customer_id: user?.customer_id,
          project_id: user?.project_id,
        }),
      })
      setMessage(
        `Invite queued (${result.mail.status}). ` +
          (result.otp_demo ? `Demo OTP ${result.otp_demo}. ` : '') +
          `Gate: ${result.invite_url}`,
      )
      setInviteEmail('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed')
    }
  }

  async function onCreateAgent(e: FormEvent) {
    e.preventDefault()
    if (!user?.customer_id || !user.project_id) {
      setError('Bind a customer/project scope first')
      return
    }
    setError(null)
    setMessage(null)
    try {
      const challenge = await api<{
        challenge_id: string
        otp_demo?: string
      }>('/api/afos360/admin/otp/start', {
        method: 'POST',
        body: JSON.stringify({ purpose: 'create_agent' }),
      })
      const otp =
        challenge.otp_demo ||
        window.prompt('Enter the SendGrid OTP emailed to you for create_agent') ||
        ''
      if (!otp) throw new Error('OTP required')
      const agent = await api<AgentRow>('/api/afos360/admin/agents', {
        method: 'POST',
        body: JSON.stringify({
          name: agentName,
          customer_id: user.customer_id,
          project_id: user.project_id,
          otp_challenge_id: challenge.challenge_id,
          otp,
        }),
      })
      setMessage(`Agent ${agent.agent_id} created with email-OTP 2FA policy.`)
      setAgentName('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Agent create failed')
    }
  }

  async function onSaveKey(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const secrets: Record<string, string> = {}
      if (keySecret.trim()) secrets[keySecretField] = keySecret.trim()
      const publicCfg: Record<string, string> = {}
      if (keyProvider === 'github') {
        const ownerRepo = keyPublic.trim()
        if (ownerRepo.includes('/')) {
          const [owner, repo] = ownerRepo.split('/', 2)
          if (owner) publicCfg.owner = owner
          if (repo) publicCfg.repo = repo
        } else if (ownerRepo) {
          publicCfg.owner = ownerRepo
          if (ghRepo.trim()) publicCfg.repo = ghRepo.trim()
        } else if (ghRepo.trim()) {
          publicCfg.repo = ghRepo.trim()
        }
        if (ghBranch.trim()) publicCfg.default_branch = ghBranch.trim()
      } else if (keyPublic.trim()) {
        if (['zendesk'].includes(keyProvider)) publicCfg.subdomain = keyPublic.trim()
        else if (['freshdesk'].includes(keyProvider)) publicCfg.domain = keyPublic.trim()
        else if (['jira'].includes(keyProvider)) publicCfg.base_url = keyPublic.trim()
        else if (['aws', 'bedrock'].includes(keyProvider)) publicCfg.region = keyPublic.trim()
        else if (keyProvider === 'gcp') publicCfg.project_id = keyPublic.trim()
        else if (keyProvider === 'mcp_bridge') publicCfg.endpoint = keyPublic.trim()
        else if (keyProvider === 'slack') publicCfg.workspace = keyPublic.trim()
        else publicCfg.region = keyPublic.trim()
      }
      await api('/api/afos360/v1/connectors', {
        method: 'POST',
        body: JSON.stringify({
          provider: keyProvider,
          enabled: true,
          secrets,
          public: publicCfg,
          scope: keyScope,
        }),
      })
      const tested = await api<{ ok: boolean; detail: string }>(
        '/api/afos360/v1/connectors/test',
        {
          method: 'POST',
          body: JSON.stringify({ provider: keyProvider, scope: keyScope }),
        },
      )
      setMessage(
        `${keyScope}/${keyProvider} saved · test ${tested.ok ? 'ok' : 'failed'} (${tested.detail})`,
      )
      setKeySecret('')
      await refresh(keyScope)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Key save failed')
    } finally {
      setBusy(false)
    }
  }

  async function onTestKey(provider: string) {
    setBusy(true)
    setError(null)
    try {
      const tested = await api<{ ok: boolean; detail: string }>(
        '/api/afos360/v1/connectors/test',
        {
          method: 'POST',
          body: JSON.stringify({ provider, scope: keyScope }),
        },
      )
      setMessage(
        `${keyScope}/${provider} test ${tested.ok ? 'ok' : 'failed'} (${tested.detail})`,
      )
      await refresh(keyScope)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed')
    } finally {
      setBusy(false)
    }
  }

  function cloudSecretFields(cloud: string): { primary: string; secondary?: string; publicHint: string } {
    if (cloud === 'aws') {
      return {
        primary: 'access_key_id',
        secondary: 'secret_access_key',
        publicHint: 'region (e.g. us-east-1)',
      }
    }
    if (cloud === 'gcp') {
      return { primary: 'service_account_json', publicHint: 'GCP project_id' }
    }
    if (cloud === 'azure') {
      return {
        primary: 'client_id',
        secondary: 'client_secret',
        publicHint: 'subscription_id',
      }
    }
    if (cloud === 'cloudflare') {
      return { primary: 'api_token', publicHint: 'account_id' }
    }
    return { primary: 'api_token', publicHint: 'org / team id (optional)' }
  }

  async function onSaveDeploy(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const fields = cloudSecretFields(primaryCloud)
      const secrets: Record<string, string> = {}
      if (cloudSecret.trim()) secrets[fields.primary] = cloudSecret.trim()
      if (fields.secondary && cloudSecret2.trim()) {
        secrets[fields.secondary] = cloudSecret2.trim()
      }
      const publicCfg: Record<string, string> = {}
      if (cloudPublic.trim()) {
        if (primaryCloud === 'aws') publicCfg.region = cloudPublic.trim()
        else if (primaryCloud === 'gcp') publicCfg.project_id = cloudPublic.trim()
        else if (primaryCloud === 'azure') publicCfg.subscription_id = cloudPublic.trim()
        else if (primaryCloud === 'cloudflare') publicCfg.account_id = cloudPublic.trim()
        else if (primaryCloud === 'fly') publicCfg.org = cloudPublic.trim()
        else if (primaryCloud === 'vercel') publicCfg.team_id = cloudPublic.trim()
      }
      if (primaryRegion) publicCfg.region = publicCfg.region || primaryRegion
      const result = await api<{
        prefs: { primary_cloud: string }
        key_test?: { ok: boolean; detail: string } | null
        readiness?: DeployReadiness
      }>('/api/afos360/deploy/select', {
        method: 'POST',
        body: JSON.stringify({
          cloud: primaryCloud,
          region: primaryRegion,
          secondary_cloud: secondaryCloud || null,
          scope: keyScope,
          secrets,
          public: publicCfg,
          test_keys: Object.keys(secrets).length > 0,
        }),
      })
      const testNote = result.key_test
        ? ` · key test ${result.key_test.ok ? 'ok' : 'fail'} (${result.key_test.detail})`
        : ''
      setMessage(
        `Deploy ${result.prefs.primary_cloud}/${primaryRegion} saved (${keyScope})${testNote}` +
          (result.readiness?.ready ? ' · factory ready' : ' · add/test keys to complete'),
      )
      setCloudSecret('')
      setCloudSecret2('')
      await refresh(keyScope)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deploy save failed')
    } finally {
      setBusy(false)
    }
  }

  async function onSaveMcp(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api('/api/afos360/v1/mcp', {
        method: 'POST',
        body: JSON.stringify({
          server_id: mcpId,
          template_id: mcpTemplate,
          endpoint: mcpEndpoint || null,
          enabled: true,
          scope: keyScope,
        }),
      })
      const tested = await api<{ ok: boolean; detail: string }>('/api/afos360/v1/mcp/test', {
        method: 'POST',
        body: JSON.stringify({ server_id: mcpId, scope: keyScope }),
      })
      setMessage(`MCP ${mcpId} saved · test ${tested.ok ? 'ok' : 'failed'} (${tested.detail})`)
      await refresh(keyScope)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MCP save failed')
    } finally {
      setBusy(false)
    }
  }

  async function onAutoWireMcp() {
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ count: number }>('/api/afos360/v1/mcp/auto-wire', {
        method: 'POST',
        body: JSON.stringify({ scope: keyScope }),
      })
      setMessage(`Auto-wired ${result.count} MCP server(s) from connectors (ZD/Jira/GitHub/…)`)
      await refresh(keyScope)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MCP auto-wire failed')
    } finally {
      setBusy(false)
    }
  }

  async function onSaveWebhook(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ url?: string; secret_once?: string }>(
        '/api/afos360/v1/webhooks',
        {
          method: 'POST',
          body: JSON.stringify({
            url: webhookUrl,
            enabled: true,
            rotate_secret: true,
            scope: keyScope,
          }),
        },
      )
      setMessage(
        `Webhook saved for ${result.url}` +
          (result.secret_once ? ` · HMAC secret (copy once): ${result.secret_once}` : ''),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Webhook save failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppChrome>
      <main className="afos-page grid gap-6 lg:grid-cols-2">
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-4 sm:p-6 lg:col-span-2"
        >
          <div className="mb-2 flex flex-wrap items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold sm:text-2xl">
            <Shield className="h-5 w-5 text-[var(--color-cyan)]" />
            Command authority
          </div>
          <p className="mb-3 text-xs text-[var(--color-mist)] sm:text-sm">
            Super admin gate · {user?.email}
          </p>
          <p className="text-sm text-[var(--color-mist)]">
            Tenant setup: users, AI/integration keys (each with Test), cloud deploy choice,
            MCP bridges, and ticket SDLC. Secrets never leave the gitignored auth tree.
            Cursor Cloud work is not mobile-only — same PR from laptop. Docs live in{' '}
            <code className="text-[var(--color-cyan)]">docs/</code> on GitHub; use{' '}
            <code className="text-[var(--color-cyan)]">review-repo</code> to mine a laptop
            repo after you push it.
          </p>
          {message && (
            <p className="mt-3 rounded-2xl border border-[rgba(46,230,197,0.35)] bg-[rgba(46,230,197,0.08)] px-3 py-2 text-sm text-[var(--color-cyan)]">
              {message}
            </p>
          )}
          {error && (
            <p className="mt-3 rounded-2xl border border-[rgba(255,107,138,0.35)] bg-[rgba(255,107,138,0.08)] px-3 py-2 text-sm text-[var(--color-rose)]">
              {error}
            </p>
          )}
        </motion.section>

        <section className="glass rounded-3xl p-6">
          <h2 className="mb-4 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <UserPlus className="h-5 w-5 text-[var(--color-amber)]" />
            Invite with OTP
          </h2>
          {user?.can_invite ? (
            <form className="space-y-3" onSubmit={onInvite}>
              <input
                className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 outline-none focus:border-[var(--color-cyan)]"
                placeholder="agent@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                type="email"
                required
              />
              <select
                className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option value="agent_admin">agent_admin</option>
                <option value="tenant_admin">tenant_admin</option>
                <option value="builder">builder</option>
                <option value="viewer">viewer</option>
              </select>
              <button
                type="submit"
                className="neon-ring w-full rounded-full bg-[var(--color-foam)] px-4 py-3 font-semibold text-[var(--color-void)]"
              >
                Send SendGrid OTP invite
              </button>
            </form>
          ) : (
            <p className="text-sm text-[var(--color-mist)]">
              AFOS 360 is invite only during early access. Only a platform super admin can send
              invites; ask your platform admin to invite new members.
            </p>
          )}
        </section>

        {user?.is_super_admin && (
          <section className="glass rounded-3xl p-6">
            <h2 className="mb-4 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
              <UserPlus className="h-5 w-5 text-[var(--color-cyan)]" />
              Early-access requests
            </h2>
            {accessRequests.length === 0 ? (
              <p className="text-sm text-[var(--color-mist)]">No requests yet.</p>
            ) : (
              <ul className="space-y-3">
                {accessRequests.map((r) => (
                  <li key={r.email} className="rounded-2xl border border-[var(--color-line)] p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold">{r.name || r.email}</p>
                        <p className="truncate text-[var(--color-mist)]">
                          {r.email}
                          {r.company ? ` · ${r.company}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await api('/api/afos360/admin/access-requests/dismiss', {
                              method: 'POST',
                              body: JSON.stringify({ email: r.email }),
                            })
                            setAccessRequests((rows) => rows.filter((row) => row.email !== r.email))
                          } catch (err) {
                            setError(err instanceof Error ? err.message : 'Dismiss failed')
                          }
                        }}
                        className="shrink-0 text-xs text-[var(--color-mist)] hover:underline"
                      >
                        Dismiss
                      </button>
                      {r.status === 'invited' ? (
                        <span className="shrink-0 text-xs text-[var(--color-mist)]">Invited</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setInviteEmail(r.email)
                            setInviteRole('builder')
                            setMessage(`Invite form filled for ${r.email}. Choose a role and send.`)
                          }}
                          className="shrink-0 rounded-full border border-[var(--color-line)] px-3 py-1 text-xs font-semibold"
                        >
                          Invite
                        </button>
                      )}
                    </div>
                    {r.note && <p className="mt-2 text-[var(--color-mist)]">{r.note}</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="glass rounded-3xl p-6">
          <h2 className="mb-4 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <Bot className="h-5 w-5 text-[var(--color-cyan)]" />
            Spawn agent (2FA)
          </h2>
          <form className="space-y-3" onSubmit={onCreateAgent}>
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 outline-none focus:border-[var(--color-cyan)]"
              placeholder="Agent display name"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              required
            />
            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[var(--color-line)] px-4 py-3 font-semibold"
            >
              <KeyRound className="h-4 w-4 text-[var(--color-amber)]" />
              Create with email OTP
            </button>
          </form>
          <div className="mt-4 space-y-2">
            {agents.map((agent) => (
              <div
                key={agent.agent_id}
                className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-3 py-2 text-sm"
              >
                <div className="font-semibold">{agent.name}</div>
                <div className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--color-mist)]">
                  {agent.agent_id} · {agent.status}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="glass rounded-3xl p-6 lg:col-span-2">
          <h2 className="mb-2 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <KeyRound className="h-5 w-5 text-[var(--color-amber)]" />
            API keys & integrations
          </h2>
          <p className="mb-4 text-sm text-[var(--color-mist)]">
            Administer GitHub, AI models, tickets, cloud, and collab at{' '}
            <strong className="text-[var(--color-foam)]">tenant</strong> (org-wide) or{' '}
            <strong className="text-[var(--color-foam)]">project</strong> scope. Project
            inherits missing tenant keys. Every key has a Test button; values are write-only.
          </p>
          <div className="mb-4 flex flex-wrap gap-2">
            {(['tenant', 'project'] as KeyScope[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setKeyScope(s)}
                className={
                  keyScope === s
                    ? 'rounded-full bg-[var(--color-cyan)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]'
                    : 'rounded-full border border-[var(--color-line)] px-4 py-2 text-sm'
                }
              >
                {s} scope
              </button>
            ))}
          </div>
          <form className="mb-6 grid gap-3 md:grid-cols-2 lg:grid-cols-4" onSubmit={onSaveKey}>
            <select
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={keyProvider}
              onChange={(e) => setKeyProvider(e.target.value)}
            >
              {connectors.map((c) => (
                <option key={c.provider} value={c.provider}>
                  {c.category}/{c.provider}
                </option>
              ))}
            </select>
            <input
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder={
                keyProvider === 'github'
                  ? 'owner or owner/repo'
                  : 'public hint (subdomain/region/…)'
              }
              value={keyPublic}
              onChange={(e) => setKeyPublic(e.target.value)}
            />
            {keyProvider === 'github' && !keyPublic.includes('/') && (
              <input
                className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
                placeholder="repo name"
                value={ghRepo}
                onChange={(e) => setGhRepo(e.target.value)}
              />
            )}
            {keyProvider === 'github' && (
              <input
                className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
                placeholder="default branch"
                value={ghBranch}
                onChange={(e) => setGhBranch(e.target.value)}
              />
            )}
            <input
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder={`${keySecretField} (never shown again)`}
              type="password"
              value={keySecret}
              onChange={(e) => setKeySecret(e.target.value)}
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-[var(--color-cyan)] px-4 py-3 font-semibold text-[var(--color-void)] md:col-span-2 lg:col-span-1"
            >
              Save & test ({keyScope})
            </button>
          </form>
          <div className="space-y-4">
            {CATEGORY_ORDER.map((cat) => {
              const rows = connectors.filter((c) => c.category === cat)
              if (!rows.length) return null
              return (
                <div key={cat}>
                  <div className="mb-2 text-xs uppercase tracking-wider text-[var(--color-mist)]">
                    {cat}
                    {cat === 'scm' ? ' · GitHub (tenant + project)' : ''}
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {rows.map((c) => (
                      <div
                        key={c.provider}
                        className="flex items-center justify-between gap-2 rounded-2xl border border-[var(--color-line)] bg-black/20 px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <div className="font-semibold">{c.provider}</div>
                          <div className="truncate text-[11px] text-[var(--color-mist)]">
                            {c.configured_here
                              ? 'configured here'
                              : c.inherited_from
                                ? `inherited · ${c.inherited_from}`
                                : c.configured
                                  ? 'configured'
                                  : 'empty'}
                            {c.public?.owner && c.public?.repo
                              ? ` · ${c.public.owner}/${c.public.repo}`
                              : ''}
                            {c.last_test_ok === true
                              ? ' · ok'
                              : c.last_test_ok === false
                                ? ` · fail (${c.last_test_detail || ''})`
                                : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={busy || !c.configured}
                          onClick={() => void onTestKey(c.provider)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs"
                        >
                          <FlaskConical className="h-3 w-3 text-[var(--color-cyan)]" />
                          Test
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
          <Link
            to="/tickets"
            className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--color-cyan)]"
          >
            <PlugZap className="h-4 w-4" />
            Open Tickets → SDLC pull & approve
          </Link>
        </section>

        <section className="glass rounded-3xl p-6 lg:col-span-2">
          <h2 className="mb-2 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <Cloud className="h-5 w-5 text-[var(--color-cyan)]" />
            Deploy factory — cloud + keys
          </h2>
          <p className="mb-3 text-sm text-[var(--color-mist)]">
            First-class tenant choice: <strong className="text-[var(--color-foam)]">AWS</strong> or{' '}
            <strong className="text-[var(--color-foam)]">GCP</strong> (plus Azure & edge). AFOS
            platform home stays AWS; your apps deploy where you choose. Save preference and cloud
            keys together — every key is Tested.
            {deployReady?.ready ? (
              <span className="ml-2 text-[var(--color-cyan)]">Ready for autonomous deploy plans.</span>
            ) : (
              <span className="ml-2">Select cloud, paste keys, Save & test.</span>
            )}
          </p>
          <div className="mb-4 grid gap-2 sm:grid-cols-3">
            {(deployReady?.hyperscalers?.length
              ? deployReady.hyperscalers
              : targets.filter((t) => t.tier === 'hyperscaler')
            ).map((t) => {
              const cloud = 'cloud' in t ? t.cloud : (t as DeployTarget).cloud
              const label = t.label
              const allowed =
                'allowed_on_plan' in t ? Boolean(t.allowed_on_plan) : true
              const selected = primaryCloud === cloud
              return (
                <button
                  key={cloud}
                  type="button"
                  disabled={!allowed}
                  onClick={() => {
                    setPrimaryCloud(cloud)
                    const regions =
                      ('regions_default' in t && t.regions_default) ||
                      targets.find((x) => x.cloud === cloud)?.regions_default ||
                      []
                    if (regions[0]) setPrimaryRegion(regions[0])
                  }}
                  className={
                    selected
                      ? 'rounded-2xl border border-[var(--color-cyan)] bg-[rgba(46,230,197,0.12)] px-4 py-3 text-left'
                      : 'rounded-2xl border border-[var(--color-line)] bg-black/20 px-4 py-3 text-left'
                  }
                >
                  <div className="font-semibold">{label}</div>
                  <div className="mt-1 text-[11px] text-[var(--color-mist)]">
                    {allowed ? 'on plan' : 'upgrade'}
                    {'keys_configured' in t && t.keys_configured ? ' · keys ok' : ' · keys needed'}
                    {'last_test_ok' in t && t.last_test_ok === true ? ' · tested' : ''}
                  </div>
                </button>
              )
            })}
          </div>
          <div className="mb-4 flex flex-wrap gap-2">
            {(deployReady?.edge_and_alt || targets.filter((t) => t.tier !== 'hyperscaler')).map(
              (t) => {
                const cloud = t.cloud
                const allowed = t.allowed_on_plan !== false
                return (
                  <button
                    key={cloud}
                    type="button"
                    disabled={!allowed}
                    onClick={() => setPrimaryCloud(cloud)}
                    className={
                      primaryCloud === cloud
                        ? 'rounded-full bg-[var(--color-foam)] px-3 py-1.5 text-xs font-semibold text-[var(--color-void)]'
                        : 'rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs'
                    }
                  >
                    {t.label}
                  </button>
                )
              },
            )}
          </div>
          <form className="grid gap-3 md:grid-cols-2" onSubmit={onSaveDeploy}>
            <select
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={primaryRegion}
              onChange={(e) => setPrimaryRegion(e.target.value)}
            >
              {(
                targets.find((t) => t.cloud === primaryCloud)?.regions_default || [
                  primaryRegion,
                ]
              ).map((r) => (
                <option key={r} value={r}>
                  region: {r}
                </option>
              ))}
            </select>
            <select
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={secondaryCloud}
              onChange={(e) => setSecondaryCloud(e.target.value)}
            >
              <option value="">secondary cloud (optional)</option>
              {targets
                .filter((t) => t.cloud !== primaryCloud && t.allowed_on_plan)
                .map((t) => (
                  <option key={t.cloud} value={t.cloud}>
                    {t.label}
                  </option>
                ))}
            </select>
            <input
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder={cloudSecretFields(primaryCloud).publicHint}
              value={cloudPublic}
              onChange={(e) => setCloudPublic(e.target.value)}
            />
            <input
              className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              type="password"
              autoComplete="off"
              placeholder={`${cloudSecretFields(primaryCloud).primary} (never shown again)`}
              value={cloudSecret}
              onChange={(e) => setCloudSecret(e.target.value)}
            />
            {cloudSecretFields(primaryCloud).secondary && (
              <input
                className="rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 md:col-span-2"
                type="password"
                autoComplete="off"
                placeholder={`${cloudSecretFields(primaryCloud).secondary} (never shown again)`}
                value={cloudSecret2}
                onChange={(e) => setCloudSecret2(e.target.value)}
              />
            )}
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-[var(--color-cyan)] px-4 py-3 font-semibold text-[var(--color-void)] md:col-span-2"
            >
              Save {primaryCloud} preference + keys & test ({keyScope})
            </button>
          </form>
          <p className="mt-3 text-xs text-[var(--color-mist)]">
            Also wired: Azure, Fly.io, Vercel, DigitalOcean, Railway, Cloudflare Workers — edge
            POCs and indie stacks without leaving the autonomous factory.
          </p>
        </section>

        <section className="glass rounded-3xl p-6">
          <h2 className="mb-2 flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <Server className="h-5 w-5 text-[var(--color-amber)]" />
            MCP registry
          </h2>
          <p className="mb-4 text-sm text-[var(--color-mist)]">
            Live MCP tools (search/get tickets, Jira projects, GitHub PRs) beat a generic bridge —
            discovery already calls them. Read-only by default; auto-wire from configured
            connectors.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void onAutoWireMcp()}
            className="mb-3 w-full rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold"
          >
            Auto-wire MCP from Zendesk/Jira/GitHub keys
          </button>
          <form className="space-y-3" onSubmit={onSaveMcp}>
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="server id"
              value={mcpId}
              onChange={(e) => setMcpId(e.target.value)}
              required
            />
            <select
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={mcpTemplate}
              onChange={(e) => setMcpTemplate(e.target.value)}
            >
              {mcpTemplates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="HTTP endpoint (optional)"
              value={mcpEndpoint}
              onChange={(e) => setMcpEndpoint(e.target.value)}
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-full bg-[var(--color-foam)] px-4 py-3 font-semibold text-[var(--color-void)]"
            >
              Save MCP & test
            </button>
          </form>
          <div className="mt-4 space-y-2">
            {mcpServers.map((s) => (
              <div
                key={s.server_id}
                className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-3 py-2 text-sm"
              >
                <div className="font-semibold">{s.label}</div>
                <div className="text-[11px] text-[var(--color-mist)]">
                  {s.server_id} · {s.template_id}
                  {s.last_test_ok === true
                    ? ' · test ok'
                    : s.last_test_ok === false
                      ? ` · test fail`
                      : ''}
                </div>
              </div>
            ))}
          </div>
          <form className="mt-4 space-y-2 border-t border-[var(--color-line)] pt-4" onSubmit={onSaveWebhook}>
            <div className="text-xs uppercase tracking-wider text-[var(--color-mist)]">
              Outbound HMAC webhooks (AppForge-class API events)
            </div>
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="https://hooks.example.com/afos"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-full border border-[var(--color-line)] px-4 py-2 text-sm font-semibold"
            >
              Save webhook + rotate secret
            </button>
          </form>
        </section>

        <section className="glass rounded-3xl p-6 lg:col-span-2">
          <h2 className="mb-4 font-[family-name:var(--font-display)] text-xl font-bold">
            Users & permissions
          </h2>
          <div className="grid gap-2 md:grid-cols-2">
            {users.map((row) => (
              <div
                key={row.email}
                className="rounded-2xl border border-[var(--color-line)] bg-black/20 px-4 py-3"
              >
                <div className="font-semibold">
                  {row.name}{' '}
                  <span className="text-xs text-[var(--color-mist)]">{row.email}</span>
                </div>
                <div className="mt-1 text-xs uppercase tracking-wider text-[var(--color-cyan)]">
                  {row.roles.join(' · ') || 'no roles'}
                  {row.two_factor_enabled ? ' · 2FA' : ''}
                </div>
              </div>
            ))}
          </div>
        </section>
        <div className="lg:col-span-2">
          <PlatformAdmin />
        </div>
      </main>
    </AppChrome>
  )
}
